import {
  LINK_TYPE_OPTIONS,
  isIdentifierRecalled,
  type IdentifierRecord,
  type SectorId,
} from '@myplayer/schema';
import { resolveIdentifierLifecycleState } from './products-firestore.js';
import {
  buildScheduledRulesList,
  formatScheduledDateLabel,
  isScheduledRuleExpired,
  resolveActiveRuleOverride,
  resolveAppliedScheduledChange,
  resolvePendingFiredScheduledChange,
  resolveScheduledChange,
  type ActiveRuleOverride,
  type AppliedScheduledLinkChange,
  type ScheduledLinkChange,
} from './products-schedule-helpers.js';
import type { ProductStatusFilter, ProductValidationFilter } from './products-table.js';

export {
  buildScheduledRulesList,
  formatScheduledDateLabel,
  isScheduledRuleExpired,
  resolveActiveRuleOverride,
  resolveAppliedScheduledChange,
  resolvePendingFiredScheduledChange,
  resolveScheduledChange,
  type ActiveRuleOverride,
  type AppliedScheduledLinkChange,
  type ScheduledLinkChange,
};

export type PlainProductStatus = 'LIVE' | 'SCHEDULED' | 'NEEDS_ATTENTION';
export type ProductSortOption =
  'MODIFIED_DESC' | 'MODIFIED_ASC' | 'TITLE_ASC' | 'TITLE_DESC' | 'STATUS';

export interface ProductFacetCounts {
  readonly distinctBrands: readonly string[];
  readonly brandCounts: Readonly<Record<string, number>>;
  readonly statusCounts: Readonly<Record<string, number>>;
  readonly sectorCounts: Readonly<Record<string, number>>;
  readonly validationCounts: Readonly<Record<string, number>>;
  readonly workspaceCounts: Readonly<Record<string, number>>;
}

export const PLAIN_LINK_TYPE_LABELS: Readonly<Record<string, string>> = {
  'gs1:pip': 'Product information page',
  'gs1:allergenInfo': 'Allergen information',
  'gs1:nutritionalInfo': 'Nutritional information',
  'gs1:sustainabilityInfo': 'Sustainability & recycling',
  'gs1:instructions': 'Instructions & manual',
  'gs1:safetyInfo': 'Safety information',
  'gs1:recallStatus': 'Recall notice page',
  'gs1:recipeInfo': 'Recipes & serving ideas',
  'gs1:certificationInfo': 'Certification & provenance',
  'gs1:traceability': 'Supply chain origin',
  'gs1:support': 'Customer support',
  'gs1:epil': 'Patient information leaflet',
  'gs1:declarationOfConformity': 'Declaration of conformity',
};

export function formatPlainLinkTypeLabel(linkType: string): string {
  if (PLAIN_LINK_TYPE_LABELS[linkType]) {
    return PLAIN_LINK_TYPE_LABELS[linkType];
  }
  const found = LINK_TYPE_OPTIONS.find((opt) => opt.value === linkType);
  if (found) {
    return found.label.replace(/\s*\(gs1:[^)]+\)/gi, '').trim();
  }
  return linkType.replace(/^gs1:/i, '');
}

export function resolvePlainProductStatus(rec: IdentifierRecord): PlainProductStatus {
  const recalled = isIdentifierRecalled(rec);
  const hasInvalidStatus =
    rec.validation?.status === 'INVALID' || (rec.validation?.errors?.length ?? 0) > 0;
  const effectiveUrl = (rec.draft?.defaultUrl ?? rec.defaultUrl ?? '').trim();
  if (recalled || hasInvalidStatus || effectiveUrl.length === 0) {
    return 'NEEDS_ATTENTION';
  }
  if (resolveScheduledChange(rec) !== null) {
    return 'SCHEDULED';
  }
  return 'LIVE';
}

function parseMultiFilterTokens(raw: string | undefined): readonly string[] {
  if (!raw) return [];
  const trimmed = raw.trim();
  return trimmed.length === 0 || trimmed === 'ALL'
    ? []
    : trimmed
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s !== 'ALL');
}

function matchesProductStatusToken(rec: IdentifierRecord, token: string): boolean {
  const plainStatus = resolvePlainProductStatus(rec);
  const recalled = isIdentifierRecalled(rec);
  const lifecycle = resolveIdentifierLifecycleState(rec);
  if (token === 'SCHEDULED') return plainStatus === 'SCHEDULED';
  if (token === 'NEEDS_ATTENTION') return plainStatus === 'NEEDS_ATTENTION';
  if (token === 'LIVE') return plainStatus === 'LIVE';
  if (token === 'RECALLED') return recalled;
  if (token === 'DRAFT') return !recalled && lifecycle === 'draft';
  if (token === 'PENDING') return !recalled && lifecycle === 'pending';
  if (token === 'INVALID') return lifecycle === 'invalid';
  return true;
}

export function computeProductFacetCounts(
  records: readonly IdentifierRecord[],
): ProductFacetCounts {
  const brandSet = new Set<string>();
  const brandCounts: Record<string, number> = {};
  const statusCounts: Record<string, number> = {
    LIVE: 0,
    SCHEDULED: 0,
    NEEDS_ATTENTION: 0,
    DRAFT: 0,
    PENDING: 0,
    INVALID: 0,
    RECALLED: 0,
  };
  const sectorCounts: Record<string, number> = {};
  const validationCounts: Record<string, number> = {
    VALID: 0,
    PENDING: 0,
    INVALID: 0,
    WARNING: 0,
  };
  const workspaceCounts: Record<string, number> = {};

  for (const rec of records) {
    const brand = (rec.draft?.brand ?? rec.brand ?? '').trim();
    if (brand.length > 0) {
      brandSet.add(brand);
      brandCounts[brand] = (brandCounts[brand] ?? 0) + 1;
    }
    for (const statusKey of Object.keys(statusCounts)) {
      if (matchesProductStatusToken(rec, statusKey)) {
        statusCounts[statusKey] = (statusCounts[statusKey] ?? 0) + 1;
      }
    }
    const effectiveSector = rec.sector ?? 'RETAIL_CPG';
    sectorCounts[effectiveSector] = (sectorCounts[effectiveSector] ?? 0) + 1;
    const effectiveValidation = rec.validation?.status ?? 'PENDING';
    validationCounts[effectiveValidation] = (validationCounts[effectiveValidation] ?? 0) + 1;
    if (rec.workspaceId) {
      workspaceCounts[rec.workspaceId] = (workspaceCounts[rec.workspaceId] ?? 0) + 1;
    }
  }

  return {
    distinctBrands: Array.from(brandSet).sort((a, b) => a.localeCompare(b)),
    brandCounts,
    statusCounts,
    sectorCounts,
    validationCounts,
    workspaceCounts,
  };
}

export function filterWorkspaceIdentifierRecords(params: {
  readonly records: readonly IdentifierRecord[];
  readonly searchQuery: string;
  readonly brandFilter?: string | undefined;
  readonly statusFilter: ProductStatusFilter | string;
  readonly sectorFilter: 'ALL' | SectorId | string;
  readonly validationFilter: ProductValidationFilter | string;
  readonly workspaceFilter?: string | undefined;
  readonly sortBy?: ProductSortOption | undefined;
}): readonly IdentifierRecord[] {
  const q = params.searchQuery.trim().toLowerCase();
  const wsTokens = parseMultiFilterTokens(params.workspaceFilter);
  const brandTokens = parseMultiFilterTokens(params.brandFilter);
  const statusTokens = parseMultiFilterTokens(params.statusFilter);
  const sectorTokens = parseMultiFilterTokens(params.sectorFilter);
  const validationTokens = parseMultiFilterTokens(params.validationFilter);

  const filtered = params.records.filter((rec) => {
    if (wsTokens.length > 0 && (!rec.workspaceId || !wsTokens.includes(rec.workspaceId)))
      return false;
    if (
      brandTokens.length > 0 &&
      !brandTokens.includes((rec.draft?.brand ?? rec.brand ?? '').trim())
    )
      return false;
    if (q.length > 0) {
      const matchesSearch = [
        rec.primaryKey,
        rec.identifierKey,
        rec.draft?.title ?? rec.title,
        rec.draft?.brand ?? rec.brand,
        rec.draft?.defaultUrl ?? rec.defaultUrl,
      ]
        .filter((v): v is string => typeof v === 'string')
        .some((v) => v.toLowerCase().includes(q));
      if (!matchesSearch) return false;
    }
    if (statusTokens.length > 0 && !statusTokens.some((tok) => matchesProductStatusToken(rec, tok)))
      return false;
    if (sectorTokens.length > 0 && !sectorTokens.includes(rec.sector ?? 'RETAIL_CPG')) return false;
    if (
      validationTokens.length > 0 &&
      !validationTokens.includes(rec.validation?.status ?? 'PENDING')
    )
      return false;
    return true;
  });

  if (!params.sortBy) return filtered;
  const getTitle = (r: IdentifierRecord): string =>
    (r.draft?.title ?? r.title ?? r.primaryKey).trim();
  const getChangedMs = (r: IdentifierRecord): number => {
    const iso = resolveRecordLastChangedIso(r);
    const ms = iso ? Date.parse(iso) : Number.NaN;
    return Number.isNaN(ms) ? 0 : ms;
  };

  return filtered.slice().sort((a, b) => {
    if (params.sortBy === 'MODIFIED_DESC' || params.sortBy === 'MODIFIED_ASC') {
      const diff =
        params.sortBy === 'MODIFIED_DESC'
          ? getChangedMs(b) - getChangedMs(a)
          : getChangedMs(a) - getChangedMs(b);
      return diff !== 0 ? diff : getTitle(a).localeCompare(getTitle(b));
    }
    if (params.sortBy === 'TITLE_DESC') return getTitle(b).localeCompare(getTitle(a));
    if (params.sortBy === 'STATUS') {
      const statusCmp = resolvePlainProductStatus(a).localeCompare(resolvePlainProductStatus(b));
      return statusCmp !== 0 ? statusCmp : getTitle(a).localeCompare(getTitle(b));
    }
    return getTitle(a).localeCompare(getTitle(b));
  });
}

export function resolveRecordLastChangedIso(rec: IdentifierRecord): string | undefined {
  const rawUpdatedAt = (rec as { readonly updatedAt?: unknown }).updatedAt;
  if (typeof rawUpdatedAt === 'string' && rawUpdatedAt.trim().length > 0) {
    return rawUpdatedAt.trim();
  }
  if (rawUpdatedAt !== null && typeof rawUpdatedAt === 'object') {
    const maybeToDate = (rawUpdatedAt as { readonly toDate?: unknown }).toDate;
    if (typeof maybeToDate === 'function') {
      const dateVal = (maybeToDate as () => Date).call(rawUpdatedAt);
      if (dateVal instanceof Date && !Number.isNaN(dateVal.getTime())) {
        return dateVal.toISOString();
      }
    }
    const maybeSeconds = (rawUpdatedAt as { readonly seconds?: unknown }).seconds;
    if (typeof maybeSeconds === 'number' && Number.isFinite(maybeSeconds)) {
      return new Date(maybeSeconds * 1000).toISOString();
    }
  }
  return (
    rec.recallOverride?.activatedAt ??
    rec.draft?.submittedAt ??
    rec.validation?.validatedAt ??
    undefined
  );
}

export function formatLastChangedDate(isoString: string | undefined): string {
  if (!isoString || isoString.trim().length === 0) return '—';
  const parsed = Date.parse(isoString);
  if (Number.isNaN(parsed)) return '—';
  return new Date(parsed).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function resolveRowDestinationLabel(rec: IdentifierRecord): string {
  const recalled = isIdentifierRecalled(rec);
  if (recalled && rec.recallOverride?.overrideUrl && rec.recallOverride.overrideUrl.trim()) {
    return rec.recallOverride.overrideUrl;
  }
  if (recalled) return 'Safety recall notice page';
  const activeOverride = resolveActiveRuleOverride(rec);
  if (activeOverride !== null) {
    return activeOverride.targetHref;
  }
  const pendingFired = resolvePendingFiredScheduledChange(rec);
  if (pendingFired !== null) {
    return pendingFired.targetHref;
  }
  if (rec.defaultUrl !== undefined && rec.defaultUrl.trim().length > 0) {
    return rec.defaultUrl;
  }
  return rec.draft?.defaultUrl ?? 'No destination set';
}
